import { useState, useEffect, useCallback } from "react";
import { getSessionById, subscribe, setSessionDetails } from "./adminStore";
import { pubSub } from "../ably";
import { ensureTokenRefreshLoop } from "../api/auth.js";
import { useAdminUserDetails } from "../api/adminHook.js";
import { buildModeratorClientId } from "../utils/moderatorIdentity.js";
import useCmeSessionSync from "./useCmeSessionSync";
import { queryClient } from "../api/queryClient.js";
import { buildSessionCacheKey } from "../realtime/channelRegistry.js";
import useAdminQnaSync from "./useAdminQnaSync";
import useAdminPollSync from "./useAdminPollSync";
import useAdminActiveHistorySync from "./useAdminActiveHistorySync";
import useComponentConfigHistorySync from "../pages/SectionPage/useComponentConfigHistorySync.js";
import useComponentConfig from "../pages/SectionPage/useComponentConfig.js";
import usePollControls from "../pages/PollPage/usePollControls.js";
import useQnaControls from "../pages/QnaPage/useQnaControls.js";
import useInteractiveQuizControls from "../pages/InteractiveQuizPage/useInteractiveQuizControls.js";
import useQuizPresentation from "../pages/InteractiveQuizPage/useQuizPresentation.js";
import useChatModeration from "../pages/ChatPage/useChatModeration.js";

// If this session has no cached data AND nothing ever arrives from Ably
// (e.g. its cme-common channel has genuinely never had anything published
// to it), `loading` would otherwise stay true forever - there's no other
// signal telling us to stop waiting. This is the escape hatch: give up
// after a while and show "Session not found" instead of spinning forever.
const LOADING_TIMEOUT_MS = 12000;

// Toasts on screen at once - the oldest goes when a new one would exceed it.
const MAX_TOASTS = 3;
let toastSeq = 0; // toast ids, unique for the page's lifetime

// adminStore also holds entries created by the Q&A / poll history sync before any
// cme-common data landed ({ sessionId, qna, polls } with no `data`). Adopting one of
// those as "the session" pinned SessionGate on "Loading session..." forever (no
// status, and loading=false so the timeout above never got a say). Only an entry
// that actually carries the session details counts as loaded.
function hasSessionDetails(session) {
  return !!session?.data?.detail;
}

export default function useSessionState(id, activeTab) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  // Toast alerts (components/Toaster.jsx closes each one after a few seconds).
  const [toasts, setToasts] = useState([]);

  // Stable identity: consumers list showMsg in effect deps (e.g.
  // StreamingVendorControls' vendor-list error toast). A new function every
  // render re-ran those effects → setToasts → render → … "Maximum update depth".
  // The same message again replaces the visible one (fresh timer) instead of stacking.
  const showMsg = useCallback((text, type = "success") => {
    toastSeq += 1;
    const toast = { id: toastSeq, text, type };
    setToasts((current) =>
      [...current.filter((t) => t.text !== text || t.type !== type), toast].slice(-MAX_TOASTS),
    );
  }, []);
  const dismissToast = useCallback((toastId) => {
    setToasts((current) => current.filter((t) => t.id !== toastId));
  }, []);
  // Sticky toasts: no timer and no × - each stays until its owner clears it (e.g. "Zoom
  // streaming is off" while Zoom must be streaming). Keyed by the owner's key, one per key;
  // a falsy text clears it. Not counted against MAX_TOASTS, so a burst of toasts can't push
  // one out.
  const [stickyToasts, setStickyToasts] = useState([]);
  const setStickyToast = useCallback((key, text, type = "error") => {
    setStickyToasts((current) => {
      const existing = current.find((t) => t.id === key);
      if (text ? existing?.text === text && existing?.type === type : !existing) return current;
      const rest = current.filter((t) => t.id !== key);
      return text ? [...rest, { id: key, text, type }] : rest;
    });
  }, []);

  const { data: userDetails, isLoading: isUserDetailsLoading, error: userDetailsError } = useAdminUserDetails();

  // Ably connects only as this moderator - "moderator-<sessionId>-<id>" from the user-details
  // API: wait while that call is in flight, never connect if it fails or has no id (SessionGate
  // shows userDetailsFailed / userNotVerified instead).
  const clientId = buildModeratorClientId(id, userDetails);
  const userDetailsFailed = !!userDetailsError;
  const userNotVerified = !!id && !userDetailsFailed && userDetails !== undefined && !clientId;

  useEffect(() => {
    if (userDetailsFailed) console.warn("[ably] not connecting - user details call failed");
    if (userNotVerified) console.warn("[ably] not connecting - user details carry no user id");
  }, [userDetailsFailed, userNotVerified]);

  useEffect(() => {
    if (!id) return;
    ensureTokenRefreshLoop();
  }, [id]);

  useEffect(() => {
    if (!id || !clientId) return;
    pubSub.init({
      clientId,
      sessionId: id,
      userInfo: { userId: "admin", userName: "Admin", role: "admin" },
    });
  }, [id, clientId]);

  // Tear the realtime connection down only when this session owner fully
  // unmounts — an MFE host that mounts/unmounts the app must not leak the
  // Ably socket, its timers, or the presence entry. A session-id change must
  // NOT disconnect (the init effect above swaps the provider while every
  // other component's connection/presence listeners stay registered), so
  // this effect intentionally has no id dependency.
  useEffect(() => {
    return () => {
      pubSub.disconnect();
    };
  }, []);

  useCmeSessionSync(id);
  useAdminQnaSync(id);
  useAdminPollSync(id);
  useAdminActiveHistorySync(id);
  useComponentConfigHistorySync(id);

  useEffect(() => {
    try {
      const data = getSessionById(id);
      if (hasSessionDetails(data)) {
        setSession(data);
        setLoading(false);
      } else {
        // Fallback: queryClient may already have cme-common data (e.g. from devconfig or history replay) but adminStore mirror hasn't fired yet
        const cached = queryClient.getQueryData(buildSessionCacheKey(id));
        if (cached) {
          // Ensure adminStore is populated for consistent downstream use
          setSessionDetails(id, cached);
          const after = getSessionById(id);
          if (after) {
            setSession(after);
            setLoading(false);
          }
        }
      }
      const unsub = subscribe((all) => {
        const updated = all.find((s) => s.sessionId === Number(id));
        if (hasSessionDetails(updated)) {
          setSession(updated);
          setLoading(false);
        } else {
          // Also check queryClient on store updates - handles case where mirror populates later
          const cached = queryClient.getQueryData(buildSessionCacheKey(id));
          if (cached) {
            // Keep the Q&A / polls a details-less store entry already collected.
            setSession({ ...cached, sessionId: Number(id), qna: updated?.qna || cached.qna || [], polls: updated?.polls || cached.polls || [] });
            setLoading(false);
          }
        }
      });
      // Also subscribe to queryCache directly for immediate sync if adminStore mirror is delayed
      const unsubQuery = queryClient.getQueryCache().subscribe((event) => {
        const key = event?.query?.queryKey;
        if (!Array.isArray(key) || key[0] !== "session" || Number(key[1]) !== Number(id)) return;
        const cached = queryClient.getQueryData(buildSessionCacheKey(id));
        if (cached) {
          const existing = getSessionById(id);
          if (!hasSessionDetails(existing)) {
            setSessionDetails(id, cached);
          }
          const updated = getSessionById(id) || { ...cached, sessionId: Number(id) };
          setSession(updated);
          setLoading(false);
        }
      });
      return () => {
        unsub();
        unsubQuery();
      };
    } catch (error) {
      console.error("Error loading session:", error);
      setLoading(false);
    }
  }, [id]);

  // Stop waiting after LOADING_TIMEOUT_MS even if nothing ever showed up.
  // Harmless no-op if the session already arrived by then. Counted from Ably init
  // (clientId), not mount: a slow user-details call must not end on "Session not found".
  useEffect(() => {
    if (!id || !clientId) return;
    const timeout = setTimeout(() => setLoading(false), LOADING_TIMEOUT_MS);
    return () => clearTimeout(timeout);
  }, [id, clientId]);

  const components = useComponentConfig(session, showMsg);
  const pollControls = usePollControls(session, showMsg);
  const qnaControls = useQnaControls(session);
  const interactiveQuizControls = useInteractiveQuizControls(session);
  const presentation = useQuizPresentation(interactiveQuizControls.activeInteractiveQuizzes, session);
  const chat = useChatModeration(session, activeTab, showMsg);

  const ctx = {
    session,
    loading,
    toasts,
    showMsg,
    dismissToast,
    stickyToasts,
    setStickyToast,
    userDetails,
    isUserDetailsLoading,
    userDetailsError,
    userDetailsFailed,
    userNotVerified,
    ...components,
    ...pollControls,
    ...qnaControls,
    ...interactiveQuizControls,
    ...presentation,
    ...chat,
  };

  return { ctx, loading, session, userDetailsFailed, userNotVerified };
}
